import { useMutation, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from 'constants/endpoints';
import { get, handleError, handleResponse, post, put } from './base.service';
import { APP_GG__SECRET_APP_ID, APP_GG_APP_ID } from 'constants';

export const useLoginMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.AUTH_LOGIN, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetLanguageListQuery = () =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_LANGUAGES_LIST}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['getLanguageList'],
		staleTime: Infinity,
	});

export const useSignupMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.AUTH_SIGNUP, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useForgotPasswordMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.AUTH_FORGOT_PASSWORD, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useVerifyOTPMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.AUTH_VERIFY_OTP, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useLogoutMutation = () => {
	return useMutation({
		mutationFn: () => {
			return get(API_ENDPOINTS.AUTH_LOGOUT);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useResetPasswordMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.AUTH_RESET_PASSWORD, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useSocialLoginMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.AUTH_SOCIAL_LOGIN, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useResendOTPMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.AUTH_RESEND_OTP, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useVerifyAccountMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return get(`${API_ENDPOINTS.AUTH_VERIFY_ACCOUNT}?token=${payload}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useGetAccessTokenGoogleMutation = () =>
	useMutation({
		mutationFn: async (payload: any) => {
			const tokenEndpoint = import.meta.env.VITE_GOOGLE_TOKEN_ENDPOINT;

			const params = new URLSearchParams();
			params.append('code', payload.code);
			params.append('client_id', APP_GG_APP_ID);
			params.append('client_secret', APP_GG__SECRET_APP_ID);
			params.append('redirect_uri', import.meta.env.VITE_GOOGLE_REDIRECTION_URL);
			params.append('grant_type', 'authorization_code');

			try {
				const response = await fetch(tokenEndpoint, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
					body: params,
				});

				if (!response.ok) {
					throw new Error('Failed to exchange authorization code for access token');
				}

				const data = await response.json();
				return data;
				// You can now use the access token to fetch user info or other resources
			} catch (error) {
				console.error('Error:', error);
			}
		},
		onSuccess: handleResponse, // handle successful response
		onError: handleError, // handle any errors
	});

export const useLinkedInCallbackMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return get(`${API_ENDPOINTS.GET_LINKEDIN_CALLBACK}?code=${payload?.code}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useUpdateSocialLoginMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return put(API_ENDPOINTS.AUTH_UPDATE_SOCIAL_DETAILS, payload, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${payload?.token}`,
				},
			});
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
